import React, { useState } from "react";
import { Card, Typography, Space, Row, Col, Button } from "antd";

const { Title, Paragraph, Text } = Typography;

const DataInformationKnowledge = () => {
  const [selectedExample, setSelectedExample] = useState("weather");
  const [revealLevel, setRevealLevel] = useState(1);

  const options = [
    { key: "weather", label: "Weather Report" },
    { key: "grades", label: "School Grades" },
    { key: "shopping", label: "Shopping List" },
  ];

  const examples = {
    weather: {
      title: "Weather Report",
      data: {
        raw: ["25", "30", "28", "32", "27"],
        description: "Just numbers - they could mean anything!",
      },
      information: {
        processed: "Daily temperatures this week: 25°C, 30°C, 28°C, 32°C, 27°C",
        description: "Now we know what these numbers mean!",
      },
      knowledge: {
        insight: "It's getting hotter mid-week, so pack light clothes for Wednesday and Thursday",
        description: "We understand the pattern and can make smart decisions!",
      },
    },
    grades: {
      title: "School Grades",
      data: {
        raw: ["85", "92", "78", "88", "95"],
        description: "Random numbers without any context.",
      },
      information: {
        processed: "Math test scores: 85%, 92%, 78%, 88%, 95%",
        description: "Now we understand - these are your math test scores!",
      },
      knowledge: {
        insight: "You're improving over time! Your scores went from 78% to 95%, so keep studying this way",
        description: "We see the trend and can guide future actions!",
      },
    },
    shopping: {
      title: "Shopping List",
      data: {
        raw: ["Apples", "Milk", "Bread", "Eggs", "Cheese"],
        description: "Just a list of words with no meaning yet.",
      },
      information: {
        processed: "Grocery items needed for this week: Apples (5), Milk (1 gallon), Bread (2 loaves), Eggs (1 dozen), Cheese (200g)",
        description: "Now organized with quantities!",
      },
      knowledge: {
        insight: "These items make breakfast for a week. Buy them on Monday so they're fresh all week",
        description: "We understand the purpose and can plan ahead!",
      },
    },
  };

  const consolidatedData = [
    {
      concept: "Data",
      description: "Raw facts without context - like puzzle pieces scattered on a table.",
      example: "25, 30, 28",
      realWorld: "Heart rate, blood pressure readings",
    },
    {
      concept: "Information",
      description: "Data with context and meaning - the puzzle pieces start forming a picture!",
      example: "Temperatures this week: 25°C, 30°C, 28°C",
      realWorld: "Patient has elevated vitals",
    },
    {
      concept: "Knowledge",
      description: "Understanding that guides decisions - the complete puzzle!",
      example: "It's hot mid-week, wear light clothes Wed-Thu",
      realWorld: "Recommend lifestyle changes",
    },
  ];

  const handleExampleChange = (key) => {
    setSelectedExample(key);
    setRevealLevel(1);
  };

  const currentExample = examples[selectedExample];

  return (
    <div className="page-container">
      <header style={{ textAlign: "center", marginBottom: "32px" }} className="fade-in-down">
        <Title level={1}>Data → Information → Knowledge</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          Watch how raw facts transform into smart decisions!
        </Paragraph>
      </header>

      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Consolidated Concepts and Examples - Responsive Table/List */}
        <Card title={<span style={{ color: "#0071e3" }}>Concepts & Examples</span>}>
          {/* Desktop Table View */}
          <div className="desktop-table">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ padding: "12px 16px", textAlign: "left", borderBottom: "2px solid #0071e3", width: "15%" }}>
                      <Text strong>Concept</Text>
                    </th>
                    <th style={{ padding: "12px 16px", textAlign: "left", borderBottom: "2px solid #0071e3", width: "35%" }}>
                      <Text strong>Description</Text>
                    </th>
                    <th style={{ padding: "12px 16px", textAlign: "left", borderBottom: "2px solid #0071e3", width: "25%" }}>
                      <Text strong>Simple Example</Text>
                    </th>
                    <th style={{ padding: "12px 16px", textAlign: "left", borderBottom: "2px solid #0071e3", width: "25%" }}>
                      <Text strong>Real-World Example</Text>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {consolidatedData.map((row, idx) => (
                    <tr key={idx} style={{ borderBottom: idx < consolidatedData.length - 1 ? "2px solid #f0f0f0" : "none" }}>
                      <td style={{ padding: "16px", verticalAlign: "top", textAlign: "left" }}>
                        <Text strong style={{ color: "#0071e3" }}>{row.concept}</Text>
                      </td>
                      <td style={{ padding: "16px", textAlign: "left", verticalAlign: "top" }}>
                        {row.description}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left", verticalAlign: "top" }}>
                        {row.example}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left", verticalAlign: "top" }}>
                        {row.realWorld}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-cards">
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {consolidatedData.map((item, idx) => (
                <Card key={idx} size="small" style={{ border: "1px solid #0071e3", borderRadius: "8px" }}>
                  <Text strong style={{ color: "#0071e3", fontSize: "16px" }}>{item.concept}</Text>
                  <div style={{ marginTop: "8px" }}>
                    <Text strong>Description: </Text>
                    <Text>{item.description}</Text>
                  </div>
                  <div style={{ marginTop: "8px" }}>
                    <Text strong>Simple Example: </Text>
                    <Text>{item.example}</Text>
                  </div>
                  <div style={{ marginTop: "8px" }}>
                    <Text strong>Real-World Example: </Text>
                    <Text>{item.realWorld}</Text>
                  </div>
                </Card>
              ))}
            </Space>
          </div>
        </Card>

        {/* Interactive Demo - UNCHANGED */}
        <Card title={<span style={{ color: "#0071e3" }}>Interactive Demo</span>}>
          <Paragraph>
            Choose an example and click the buttons to see how data transforms into information and knowledge
          </Paragraph>

          <Row gutter={[16, 16]} justify="center">
            {options.map((opt) => (
              <Col xs={24} sm={24} md={8} key={opt.key}>
                <Card
                  hoverable={false}
                  onClick={() => handleExampleChange(opt.key)}
                  style={{
                    cursor: "pointer",
                    border: selectedExample === opt.key ? "2px solid #667eea" : "2px solid #f0f0f0",
                    textAlign: "center",
                    fontWeight: 500,
                  }}
                >
                  {opt.label}
                </Card>
              </Col>
            ))}
          </Row>

          <Space direction="vertical" size="middle" style={{ width: "100%", marginTop: "24px" }}>
            <Card size="small" style={{ backgroundColor: "#fff1f0", borderColor: "#ff4d4f" }}>
              <Text strong>Data: </Text>
              <Text code>{currentExample.data.raw.join(", ")}</Text>
              <div style={{ marginTop: "8px" }}>
                <Text type="secondary">{currentExample.data.description}</Text>
              </div>
            </Card>

            {revealLevel >= 2 && (
              <>
                <Card size="small" style={{ backgroundColor: "#e6f7ff", borderColor: "#1890ff" }}>
                  <Text strong>Information: </Text>
                  <Text>{currentExample.information.processed}</Text>
                  <div style={{ marginTop: "8px" }}>
                    <Text type="secondary">{currentExample.information.description}</Text>
                  </div>
                </Card>
              </>
            )}

            {revealLevel >= 3 && (
              <>
                <Card size="small" style={{ backgroundColor: "#f6ffed", borderColor: "#52c41a" }}>
                  <Text strong>Knowledge: </Text>
                  <Text>{currentExample.knowledge.insight}</Text>
                  <div style={{ marginTop: "8px" }}>
                    <Text type="secondary">{currentExample.knowledge.description}</Text>
                  </div>
                </Card>
              </>
            )}

            <div style={{ textAlign: "center", marginTop: "16px" }}>
              {revealLevel < 3 && (
                <Button
                  size="large"
                  type="primary"
                  style={{ border: "1px solid #0071e3" }}
                  onClick={() => setRevealLevel(revealLevel + 1)}
                >
                  {revealLevel === 1 ? "Show Information" : "Show Knowledge"}
                </Button>
              )}
              {revealLevel === 3 && (
                <Button size="large" type="primary" ghost onClick={() => setRevealLevel(1)}>
                  Reset
                </Button>
              )}
            </div>
          </Space>
        </Card>
      </Space>

      <style>{`
        @media (min-width: 769px) {
          .desktop-table {
            display: block;
          }
          .mobile-cards {
            display: none;
          }
        }
        @media (max-width: 768px) {
          .desktop-table {
            display: none;
          }
          .mobile-cards {
            display: block;
          }
        }
      `}</style>
    </div>
  );
};

export default DataInformationKnowledge;